import { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { FileText, Download, Upload, AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useMyWorkerRecord, useMyDocuments, useMyCertifications } from "@/hooks/useWorkerPortal";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

// ── Upload Certification Dialog ───────────────────────────────────────────
function UploadCertDialog({
  open,
  onClose,
  workerId,
}: {
  open: boolean;
  onClose: () => void;
  workerId: string;
}) {
  const [certName, setCertName] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const handleClose = () => {
    setCertName("");
    setExpiryDate("");
    setFile(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!certName.trim()) return toast.error("Certification name is required");
    setUploading(true);
    try {
      let file_url: string | null = null;
      if (file) {
        const ext = file.name.split(".").pop() ?? "pdf";
        const path = `workers/${workerId}/certs/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("worker-documents")
          .upload(path, file, { upsert: false, contentType: file.type });
        if (upErr) throw upErr;
        const { data: { publicUrl } } = supabase.storage.from("worker-documents").getPublicUrl(path);
        file_url = publicUrl;
      }
      const { error } = await supabase.from("personnel_certifications").insert({
        worker_id: workerId,
        certification_name: certName.trim(),
        expiry_date: expiryDate || null,
        document_url: file_url,
        is_verified: false,
      } as any);
      if (error) throw error;
      toast.success("Certification uploaded");
      qc.invalidateQueries({ queryKey: ["my_certifications", workerId] });
      handleClose();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to upload certification");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Upload Certification</DialogTitle>
          <DialogDescription>
            Add a certification or license to your profile.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="cert-name">Certification Name <span className="text-destructive">*</span></Label>
            <Input
              id="cert-name"
              placeholder="e.g. OSHA 10, First Aid, Forklift"
              value={certName}
              onChange={(e) => setCertName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cert-expiry">Expiry Date (optional)</Label>
            <Input
              id="cert-expiry"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Document (optional)</Label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-1" />
                {file ? file.name : "Choose file"}
              </Button>
              {file && (
                <button
                  className="text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => setFile(null)}
                >
                  Remove
                </button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted-foreground">PDF, JPG, or PNG (max 10 MB)</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={uploading || !certName.trim()}>
            {uploading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
const WorkerDocuments = () => {
  const { data: worker, isLoading: wl } = useMyWorkerRecord();
  const vendorId = worker?.vendor_id;
  const { data: docs, isLoading: dl } = useMyDocuments(vendorId || undefined);
  const { data: certs = [], isLoading: cl } = useMyCertifications(worker?.id);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const isMobile = useIsMobile();

  const i9 = docs?.i9;
  const everify = docs?.everify;
  const agreements = docs?.agreements ?? [];
  const isTNC = everify?.case_status === "tentative_nonconfirmation";

  const handleDownload = (url?: string | null, name?: string) => {
    if (!url) return toast.error("Document is not available for download");
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = name ?? "document";
    a.click();
  };

  if (wl || dl || cl) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">My Documents</h1>

      {/* TNC Alert */}
      {isTNC && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-destructive">ACTION REQUIRED — E-Verify</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your employer has received a Tentative Non-Confirmation on your work authorization.
                  You have 8 business days to contest. Contact your Project Manager immediately.
                </p>
                <Button size="sm" variant="destructive" className="mt-2">Contact PM</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Required Documents Status */}
      <Card>
        <CardHeader><CardTitle className="text-base">Required Documents</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {/* I-9 */}
          <div className={`flex ${isMobile ? "flex-col gap-1" : "items-center gap-3"} text-sm`}>
            <div className="flex items-center gap-2">
              {i9?.i9_status === "complete" ? (
                <Badge variant="default" className="text-[10px]">✅ Verified</Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px]">⏳ Pending</Badge>
              )}
              <span className="font-medium">I-9 Verification</span>
            </div>
            {i9?.section2_verified_at && (
              <span className="text-muted-foreground text-xs">
                Verified — {format(new Date(i9.section2_verified_at), "MMM d, yyyy")}
              </span>
            )}
          </div>

          {/* W9 */}
          <div className={`flex ${isMobile ? "flex-col gap-1" : "items-center gap-3"} text-sm`}>
            <div className="flex items-center gap-2">
              <Badge variant="default" className="text-[10px]">✅ On file</Badge>
              <span className="font-medium">W9</span>
            </div>
            <span className="text-muted-foreground text-xs">Tax Year {new Date().getFullYear()}</span>
          </div>

          {/* ICA */}
          {agreements.length > 0 && (
            <div className={`flex ${isMobile ? "flex-col gap-1" : "items-center gap-3"} text-sm`}>
              <div className="flex items-center gap-2">
                <Badge variant="default" className="text-[10px]">✅ Signed</Badge>
                <span className="font-medium">Independent Contractor Agreement</span>
              </div>
              <span className="text-muted-foreground text-xs">
                {format(new Date(agreements[0].acknowledged_at), "MMM d, yyyy")}
              </span>
            </div>
          )}

          {/* E-Verify */}
          <div className={`flex ${isMobile ? "flex-col gap-1" : "items-center gap-3"} text-sm`}>
            <div className="flex items-center gap-2">
              {everify?.case_status === "authorized" ? (
                <Badge variant="default" className="text-[10px]">✅ Authorized</Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px]">⏳ {everify?.case_status || "Pending"}</Badge>
              )}
              <span className="font-medium">E-Verify</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents on file */}
      <Card>
        <CardHeader><CardTitle className="text-base">Documents on File</CardTitle></CardHeader>
        <CardContent>
          {agreements.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No additional documents on file.</p>
          ) : (
            <div className="space-y-2">
              {agreements.map((a: any) => (
                <div key={a.id} className={`flex ${isMobile ? "flex-col gap-1.5" : "items-center justify-between"} text-sm`}>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span>{a.document_name || a.agreement_type}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      Signed: {format(new Date(a.acknowledged_at), "MMM d, yyyy")}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Download document"
                      onClick={() => handleDownload(a.document_url ?? a.file_url, a.document_name ?? a.agreement_type)}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certifications */}
      <Card>
        <CardHeader className={`flex ${isMobile ? "flex-col gap-2" : "flex-row items-center justify-between"}`}>
          <CardTitle className="text-base">Certifications</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setCertDialogOpen(true)} className={isMobile ? "w-full" : ""}>
            <Upload className="h-4 w-4 mr-1" /> Upload Certification
          </Button>
        </CardHeader>
        <CardContent>
          {certs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No certifications uploaded yet.</p>
          ) : (
            <div className="space-y-2">
              {certs.map((c: any) => (
                <div key={c.id} className={`flex ${isMobile ? "flex-col gap-1.5" : "items-center justify-between"} text-sm`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-medium">{c.certification_name}</span>
                    {c.expiry_date && (
                      <span className="text-xs text-muted-foreground">
                        Expires {format(new Date(c.expiry_date), "MMM yyyy")}
                      </span>
                    )}
                    {c.is_verified && <Badge variant="default" className="text-[10px]">Verified</Badge>}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={c.document_url ? "Download" : "No file attached"}
                    onClick={() => handleDownload(c.document_url, c.certification_name)}
                    disabled={!c.document_url}
                    className={isMobile ? "self-end" : ""}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {worker && (
        <UploadCertDialog
          open={certDialogOpen}
          onClose={() => setCertDialogOpen(false)}
          workerId={worker.id}
        />
      )}
    </div>
  );
};

export default WorkerDocuments;
